import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';

export interface PersistentError {
  id: string;
  severity: 'error' | 'warn' | 'info';
  summary: string;
  detail: string;
  timestamp: Date;
  sticky?: boolean;
  closable?: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class PersistentErrorService {
  private errorsSubject = new BehaviorSubject<PersistentError[]>([]);
  public errors$: Observable<PersistentError[]> = this.errorsSubject.asObservable();

  constructor() {}

  /**
   * Add a persistent error message that survives route changes
   */
  addError(severity: 'error' | 'warn' | 'info', summary: string, detail: string, sticky: boolean = true, closable: boolean = true): string {
    const error: PersistentError = {
      id: this.generateId(),
      severity,
      summary,
      detail,
      timestamp: new Date(),
      sticky,
      closable
    };

    const currentErrors = this.errorsSubject.value;
    this.errorsSubject.next([...currentErrors, error]);
    
    return error.id;
  }

  /**
   * Add a permission error with predefined styling
   */
  addPermissionError(message: string, requiredPermission?: string): string {
    const detail = requiredPermission 
      ? `${message} Required permission: ${requiredPermission}`
      : message;
    
    return this.addError(
      'error',
      'Access Denied',
      detail,
      true, // sticky
      true  // closable
    );
  }

  /**
   * Remove a specific error by ID
   */
  removeError(errorId: string): void {
    const currentErrors = this.errorsSubject.value;
    const filteredErrors = currentErrors.filter(error => error.id !== errorId);
    this.errorsSubject.next(filteredErrors);
  }

  /**
   * Clear all errors
   */
  clearAllErrors(): void {
    this.errorsSubject.next([]);
  }

  /**
   * Clear errors older than specified minutes
   */
  clearOldErrors(minutes: number = 5): void {
    const cutoffTime = new Date(Date.now() - minutes * 60 * 1000);
    const currentErrors = this.errorsSubject.value;
    const filteredErrors = currentErrors.filter(error => error.timestamp > cutoffTime);
    this.errorsSubject.next(filteredErrors);
  }

  /**
   * Get current errors
   */
  getCurrentErrors(): PersistentError[] {
    return this.errorsSubject.value;
  }

  /**
   * Check if there are any errors
   */
  hasErrors(): boolean {
    return this.errorsSubject.value.length > 0;
  }

  private generateId(): string {
    return 'error_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
  }
}
